// src/hooks/useReportCustomization.ts
// Report customization state (columns, filters, header/footer, cell, colours).
// Persists to localStorage per-report so a reload keeps the user's view.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_CUSTOMIZATION } from '../pages/Accounting/Reports/AccountList/constants';
import type { ReportCustomization } from '../pages/Accounting/Reports/AccountList/types';

interface UseReportCustomizationReturn {
  customization: ReportCustomization;
  draft: ReportCustomization;
  isCustomized: boolean;
  updateDraft: (patch: Partial<ReportCustomization>) => void;
  applyChanges: () => void;
  resetToDefault: () => void;
  cancelDraft: () => void;
}

const STORAGE_KEY_PREFIX = 'novala:report-customization:';

function loadCustomization(reportKey: string): ReportCustomization {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + reportKey);
    if (raw) return JSON.parse(raw) as ReportCustomization;
  } catch (e) {
    // ignore
  }
  return DEFAULT_CUSTOMIZATION;
}

function saveCustomization(reportKey: string, c: ReportCustomization): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + reportKey, JSON.stringify(c));
  } catch (e) {
    // ignore
  }
}

export function useReportCustomization(reportKey: string): UseReportCustomizationReturn {
  const [customization, setCustomization] = useState<ReportCustomization>(function () { return loadCustomization(reportKey); });
  const [draft, setDraft] = useState<ReportCustomization>(customization);

  useEffect(function () { setDraft(customization); }, [customization]);

  const isCustomized = useMemo(function () {
    return JSON.stringify(customization) !== JSON.stringify(DEFAULT_CUSTOMIZATION);
  }, [customization]);

  const updateDraft = useCallback(function (patch: Partial<ReportCustomization>) {
    setDraft(function (d) { return { ...d, ...patch }; });
  }, []);

  const applyChanges = useCallback(function () {
    setCustomization(draft);
    saveCustomization(reportKey, draft);
  }, [draft, reportKey]);

  const resetToDefault = useCallback(function () {
    setCustomization(DEFAULT_CUSTOMIZATION);
    setDraft(DEFAULT_CUSTOMIZATION);
    saveCustomization(reportKey, DEFAULT_CUSTOMIZATION);
  }, [reportKey]);

  const cancelDraft = useCallback(function () {
    setDraft(customization);
  }, [customization]);

  return { customization, draft, isCustomized, updateDraft, applyChanges, resetToDefault, cancelDraft };
}